import { R_BLOCK, R_BLOCK_PLANET } from '../level/presets';
import type { LevelNode, PlayLevel } from '../level/schema';
import { planetsOf } from '../level/schema';
import { pointWithinOfSegment } from '../geometry/segments';
import type { Line, PlayState } from './types';

export function createPlayState(level: PlayLevel): PlayState {
  return { level, constellations: [], selection: [], selectionLines: [], alignments: [], removals: 0 };
}

export function nodeMap(level: PlayLevel): Map<number, LevelNode> {
  const map = new Map<number, LevelNode>();
  for (const node of level.nodes) map.set(node.id, node);
  return map;
}

/** How close a line may come to a star's centre before it passes over it. */
export function blockRadius(level: PlayLevel): number {
  return level.rules.blockRadius ?? R_BLOCK;
}

/** How close a line may come to a planet's centre before it passes over it. */
export function planetBlockRadius(_level: PlayLevel): number {
  return R_BLOCK_PLANET;
}

export interface DerivedState {
  nodes: Map<number, LevelNode>;
  /** Node id → the word of the submitted constellation that holds it. */
  locked: Map<number, string>;
  /** Locked or in the word in progress. */
  used: Set<number>;
  /** Every line on the sky: submitted constellations first, then the word in progress. */
  lines: Line[];
  /** Unused nodes a line passes over. */
  blocked: Set<number>;
  /** Planet id → the word of the constellation that ringed it. */
  planetsActivated: Map<number, string>;
  /** Planets not yet ringed that a line passes over: they can no longer be ringed while it stands. */
  planetsBlocked: Set<number>;
}

/** Everything the rules need to answer a tap, computed from the state. */
export function derive(state: PlayState): DerivedState {
  const nodes = nodeMap(state.level);
  const locked = new Map<number, string>();
  const lines: Line[] = [];
  const planetsActivated = new Map<number, string>();
  for (const c of state.constellations) {
    for (const id of c.nodes) locked.set(id, c.word);
    lines.push(...c.lines);
    if (c.planet) planetsActivated.set(c.planet.id, c.word);
  }
  lines.push(...state.selectionLines);
  const used = new Set<number>(locked.keys());
  for (const id of state.selection) used.add(id);
  const underLine = (p: { x: number; y: number }, r: number): boolean => {
    for (const [a, b] of lines) {
      if (pointWithinOfSegment(p, nodes.get(a)!, nodes.get(b)!, r)) return true;
    }
    return false;
  };
  const r = blockRadius(state.level);
  const rPlanet = planetBlockRadius(state.level);
  const blocked = new Set<number>();
  for (const node of state.level.nodes) {
    if (!used.has(node.id) && underLine(node, r)) blocked.add(node.id);
  }
  const planetsBlocked = new Set<number>();
  for (const planet of planetsOf(state.level)) {
    if (!planetsActivated.has(planet.id) && underLine(planet, rPlanet)) planetsBlocked.add(planet.id);
  }
  return { nodes, locked, used, lines, blocked, planetsActivated, planetsBlocked };
}

export function findConstellation(state: PlayState, word: string): number {
  return state.constellations.findIndex((c) => c.word === word);
}
